import { useState, useEffect } from "react";
import { CartModel } from "atoms/models";
import { Modal, Button, Accordion } from "react-bootstrap";
import { modalCancelService } from "services/post-modalCancel";
import { useNavigate } from "react-router-dom";
import { postCustomerSummaryPage } from "services/post-postCustomerSummary";
import { CustomerSummaryRequestModel } from "atoms/models";
import { Spinner } from "react-bootstrap";
import { useRecoilState, useSetRecoilState } from "recoil";
import { toastState, overlayState } from "atoms";

interface CancelCoreTypeService {
  coreTypeDescription: string;
  mainProduct: string;
  additionalServiceOptions: string[];
  productId: number;
}

interface BundleInformation {
  productIds: number[];
  productCoreTypes: string[];
}

const CancelServicesModal: React.FC<{ cart: CartModel; cancelFeatureFlag: Boolean }> = ({ cart, cancelFeatureFlag }) => {
  const [selectedServicesToCancel, setSelectedServicesToCancel] = useState<Array<string>>([]);
  const [cancelCoreTypeServices, setCancelCoreTypeServices] = useState<{ [key: string]: CancelCoreTypeService }>();
  const [cancelServicesModal, setCancelServicesModal] = useState(false);
  const [bundleAlert, setBundleAlert] = useState(false);
  const [bundleInformation, setBundleInformation] = useState<BundleInformation>();
  const [spinner, setSpinner] = useState(false);
  const [toast, setToast] = useRecoilState(toastState);
  const [postRequestBody, setPostRequestBody] = useState<CustomerSummaryRequestModel>();
  const setShowOverlay = useSetRecoilState(overlayState);
  const [alert, setAlert] = useState({
    show: false,
    message: "",
  });

  const navigate = useNavigate();

  const cancelServices = async () => {
    setSpinner(true);
    try {
      const response = await modalCancelService(selectedServicesToCancel);
      if (response?.["_links"]?.["ec:windowLocation"]?.href) {
        navigate(response?.["_links"]?.["ec:windowLocation"]?.href);
        setSpinner(false);
      }
    } catch (error) {
      setSpinner(false);
      setAlert({ ...alert, show: true, message: `${error}` });
    }
  };

  const firstAvailableProduct = (array: { id: number; serviceCoreTypeName: string }[]) => {
    const internetProductId = array.find((item) => item.serviceCoreTypeName === "INT" || item.serviceCoreTypeName === "HSI");

    if (internetProductId) {
      return internetProductId;
    } else if (array.length > 0) {
      return array;
    }
  };

  useEffect(() => {
    if (cart?.currentServices?.monthly) {
      const product: any = firstAvailableProduct(cart?.currentServices?.monthly);
      setPostRequestBody({ coreType: product.serviceCoreTypeName, itemType: "cancel", itemId: product.id });
    }
  }, [cart]);

  const postCustomerSummaryCancelRequest = async () => {
    if (postRequestBody) {
      setShowOverlay(true);
      try {
        const response = await postCustomerSummaryPage(postRequestBody);
        const _embedded = response["_embedded"];
        if (response.ecommerceMessageType === "modal_cancel") {
          setCancelServicesModal(true);
        }
        if (_embedded["ec:cancel-coretypes"] && Object.keys(_embedded["ec:cancel-coretypes"]).length > 0) {
          const cancelCoreTypes = _embedded["ec:cancel-coretypes"];
          const coretypeOrderPriority = ["INT", "HSI", "VID", "TV", "SEC", "TEL"];
          const sortedData: { [key: string]: CancelCoreTypeService } = {};
          for (const key of coretypeOrderPriority) {
            if (cancelCoreTypes[key]) {
              sortedData[key] = cancelCoreTypes[key];
            }
          }
          setCancelCoreTypeServices(sortedData);
        }
        if (_embedded["ec:cancel-bundle-info"] && Object.keys(_embedded["ec:cancel-bundle-info"]).length > 0) {
          setBundleInformation(_embedded["ec:cancel-bundle-info"]);
        }
        setShowOverlay(false);
      } catch (error) {
        setShowOverlay(false);
        console.log(error);
        setToast({
          ...toast,
          show: true,
          variant: "danger",
          header: "Error",
          message: "To cancel service, please empty your cart",
        });
      }
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, id: serviceId } = e.target;

    // Create an updated list of selected services based on user interaction
    const updatedSelectedServices = checked
      ? [...selectedServicesToCancel, serviceId]
      : selectedServicesToCancel.filter((service) => service !== serviceId);

    // Update the selected services state
    setSelectedServicesToCancel(updatedSelectedServices);

    // Check if a bundle alert should be shown or hidden
    handleBundleAlert(serviceId, checked, updatedSelectedServices);
  };

  const handleBundleAlert = (serviceId: string, checked: boolean, updatedServices: string[]) => {
    // Check if the currently toggled service is a bundled core type
    const isPartOfTheBundle = checked && bundleInformation?.productCoreTypes?.includes(serviceId);

    // Check if *any* selected services are bundled core types
    const hasBundledServiceSelected = updatedServices.some((coreType) => bundleInformation?.productCoreTypes.includes(coreType));

    if (isPartOfTheBundle) {
      setBundleAlert(true);
    } else if (!hasBundledServiceSelected) {
      setBundleAlert(false);
    }
  };

  const formatErrorMessage = (message: string) => {
    message = message.replace(/\nTo/g, " ");
    message = message.replace(/\\n/g, " ");
    message = message.replace("Error:", "<strong>Error:</strong>");
    return message;
  };

  return (
    <div className="cancel-services-modal-container">
      <div className="d-flex justify-content-center w-100">
        {cancelFeatureFlag ? (
          <button className="btn btn-primary btn-lg cancel-services-button" onClick={() => postCustomerSummaryCancelRequest()}>
            Cancel My Service
          </button>
        ) : null}
        <Modal show={cancelServicesModal} onHide={() => setCancelServicesModal(false)} className="cancel-services-modal" backdrop="static">
          <Modal.Body>
            <div className="w-100 text-center">
              <h1>Cancel Services</h1>
              <p className="disclaimer-text">
                <i className="fa-solid fa-circle-exclamation"></i>
                <p>Cancelling will disconnect all selected services. This may result in loss of internet, phone, and any service bundle discounts.</p>
              </p>
            </div>
            {bundleAlert ? (
              <div className="alert alert-warning bundle-discount-loss-warning" role="alert">
                <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: "30px" }}></i>
                <div>Canceling this service will remove applicable bundle discounts</div>
              </div>
            ) : null}
            {alert.show ? (
              <div className="alert alert-danger" role="alert">
                <div dangerouslySetInnerHTML={{ __html: formatErrorMessage(alert.message) }} />
              </div>
            ) : null}
            <div className="services-to-cancel">
              <div className="text-dark fw-bold mb-1">Please select which service(s) to cancel</div>
              {cancelCoreTypeServices && Object.keys(cancelCoreTypeServices).length > 0
                ? Object.entries(cancelCoreTypeServices).map(([key, service], index) => (
                    <Accordion key={index}>
                      <Accordion.Item eventKey={`${index}`} className="mb-2">
                        <Accordion.Header className={service.additionalServiceOptions.length > 0 ? "target-expand-collapse-icon" : "hide-icon"}>
                          <div className="d-flex">
                            <div className="form-check">
                              <input
                                className="form-check-input services-checkbox"
                                type="checkbox"
                                value={service.productId}
                                id={key}
                                onChange={(e) => {
                                  handleCheckboxChange(e);
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAlert({ ...alert, show: false, message: "" });
                                }}
                              />
                              <label className="form-check-label fw-bold" htmlFor={key}>
                                {service.mainProduct}
                              </label>
                            </div>
                          </div>
                        </Accordion.Header>
                        {service.additionalServiceOptions.length > 0 ? (
                          <Accordion.Body>
                            {service.additionalServiceOptions.map((option: string, optIndex: number) => (
                              <div className="ps-4 ps-5" key={optIndex}>
                                {option}
                              </div>
                            ))}
                          </Accordion.Body>
                        ) : (
                          <div></div>
                        )}
                      </Accordion.Item>
                    </Accordion>
                  ))
                : null}
            </div>
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-between">
            <Button
              variant="primary"
              onClick={() => {
                setCancelServicesModal(false);
                setSelectedServicesToCancel([]);
                setAlert({ ...alert, show: false, message: "" });
              }}
            >
              Go Back
            </Button>
            <div className="d-flex">
              {spinner ? <Spinner animation="border" className="me-2" /> : null}

              <Button variant="primary" onClick={() => cancelServices()} disabled={!selectedServicesToCancel.length}>
                Continue
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default CancelServicesModal;
